import type { ReactNode } from 'react'
import type { AppFile } from '../api'
import { RELAY_STUDIO, STREVIX_OBS, formatSize } from '../apps'

/** Shared pieces of the downloads UI: icons, download links, the error and loading rows. */

export function DownloadIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className="flex-none">
      <path d="M8 2v8M4.5 6.8 8 10.3l3.5-3.5M3 13.5h10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/** Relay Studio: a screen; Relay Link: a plug between two lines; Strevix for OBS: a puzzle piece (a plugin). */
export function AppIcon({ app, size = 22 }: { app: string; size?: number }) {
  if (app === STREVIX_OBS) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-none">
        <path
          d="M5 8h3.2a2 2 0 1 1 3.6 0H15v3.2a2 2 0 1 1 0 3.6V18H5v-3.2a2 2 0 1 0 0-3.6V8Z"
          fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
        />
      </svg>
    )
  }
  return app === RELAY_STUDIO ? (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-none">
      <rect x="4" y="5" width="16" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 19h20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-none">
      <path d="M3 12h4M17 12h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="7" y="8" width="10" height="8" rx="4" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10.5 12h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/** The OS of a file: a laptop for Mac, a monitor for Windows. */
export function OsIcon({ os, size = 22 }: { os: string; size?: number }) {
  return os === 'macos' ? (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-none">
      <rect x="4" y="5" width="16" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 19h20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-none">
      <rect x="3" y="4" width="18" height="12" rx="1" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9 20h6M12 16v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/**
 * A plain download link (<a href download>): the browser saves the file.
 * primary = the build for the user's computer.
 */
export function DownloadLink({
  file,
  primary,
  size = 'md',
  testId,
  className = '',
  children,
  onClick,
}: {
  file: AppFile
  primary?: boolean
  size?: 'sm' | 'md'
  testId?: string
  className?: string
  children: ReactNode
  onClick?: () => void
}) {
  return (
    <a
      href={file.url}
      download
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex flex-none items-center justify-center gap-2 whitespace-nowrap rounded-md border no-underline ${
        size === 'sm' ? 'min-h-[34px] px-3 text-[13px] max-md:min-h-[44px]' : 'min-h-[40px] px-4 text-sm max-md:min-h-[44px]'
      } ${primary ? 'border-live bg-live/[.12] font-semibold text-live' : 'border-line-2 font-medium text-text hover:border-muted'} ${className}`}
    >
      <DownloadIcon />
      {children}
    </a>
  )
}

export const sizeOf = (f: AppFile) => formatSize(f.size)

/** The manifest did not load: the same alert in the menu, the sheet and on /apps. */
export function AppsAlert({ onRetry, retryRole }: { onRetry: () => void; retryRole?: 'menuitem' }) {
  return (
    <div role="alert" data-testid="apps-error" className="rounded-md border border-fail/50 px-3 py-2.5 text-[13px] text-fail">
      Couldn't load the list of apps.{' '}
      <button type="button" role={retryRole} onClick={onRetry} className="min-h-[34px] font-semibold text-fail underline underline-offset-2 max-md:min-h-[44px]">
        Try again
      </button>
    </div>
  )
}

/** Placeholder rows while the list loads. */
export function AppsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div data-testid="apps-loading" aria-busy="true" aria-label="Loading the list of apps" className="flex flex-col gap-2 p-1">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-2">
          <span className="h-9 w-9 flex-none animate-pulse rounded-md bg-line" />
          <span className="flex flex-1 flex-col gap-1.5">
            <span className="h-3 w-2/5 animate-pulse rounded-md bg-line" />
            <span className="h-2.5 w-1/3 animate-pulse rounded-md bg-line/70" />
          </span>
        </div>
      ))}
    </div>
  )
}

/** "command line" next to a build without a window (wsbridge for Mac). */
export function CliBadge({ testId }: { testId?: string }) {
  return (
    <span data-testid={testId} className="rounded-md border border-line-2 px-1.5 font-mono text-[10px] font-medium tracking-[.04em] text-muted">
      command line
    </span>
  )
}

/** "YOUR COMPUTER" next to the user's build. */
export function MineBadge() {
  return <span className="font-mono text-[10px] font-semibold tracking-[.08em] text-live">YOUR COMPUTER</span>
}
